import React, { useEffect, useMemo, useState } from 'react';
import { activityLogsAPI } from '../services/api';
import { ActivityLog } from '../types';
import { formatDate } from '../utils/dateFormat';

const entityOptions = ['', 'Customer', 'Project', 'Person', 'Transaction'];

const ActivityLogPage: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');

  useEffect(() => {
    loadLogs();
  }, [entityType]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await activityLogsAPI.getActivityLogs({
        entityType: entityType || undefined,
        take: 200,
      });
      setLogs(data);
    } catch (error) {
      console.error('Failed to load activity logs', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedLogs = useMemo(() => logs, [logs]);

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Activity Log</h2>
          <p className="text-muted mb-0">Recent create, update, and delete actions for core entities.</p>
        </div>
        <div style={{ minWidth: 220 }}>
          <label className="form-label">Entity Type</label>
          <select
            className="form-select"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          >
            <option value="">All</option>
            {entityOptions.filter(Boolean).map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
              <div className="spinner-border" role="status" />
            </div>
          ) : groupedLogs.length === 0 ? (
            <div className="text-center text-muted py-5">No activity log entries found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedLogs.map(log => (
                    <tr key={log.id}>
                      <td>{formatDate(log.createdDate)} {new Date(log.createdDate).toLocaleTimeString()}</td>
                      <td>
                        <div>{log.userName || log.userEmail || 'Unknown user'}</div>
                        {log.userEmail && log.userName !== log.userEmail && (
                          <small className="text-muted">{log.userEmail}</small>
                        )}
                      </td>
                      <td>
                        <span className="badge contact-type-badge" style={{ backgroundColor: '#4b5563', color: '#fff' }}>
                          {log.actionType}
                        </span>
                      </td>
                      <td>
                        <div className="fw-bold">{log.entityType}</div>
                        <small className="text-muted">#{log.entityId} · {log.entityTitle || 'Untitled'}</small>
                        {log.entityType === 'Transaction' && log.summary && (
                          <small className="text-muted d-block">{log.summary}</small>
                        )}
                      </td>
                      <td>{log.entityType === 'Transaction' ? '—' : (log.summary || '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
